import React, { useEffect } from 'react';
import logger from '../utils/logger';
import { planetOrder, planetColors, planetSymbols, PlanetKey } from '../utils/planets';

interface PlanetLoaderProps {
  message?: string;
  className?: string;
}

const PlanetLoader: React.FC<PlanetLoaderProps> = ({
  message = "Calculating your birth chart...",
  className = ''
}) => {
  useEffect(() => {
    logger.componentMount('PlanetLoader', { message });
    return () => {
      logger.componentUnmount('PlanetLoader');
    };
  }, [message]);

  return (
    <div className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}>
      {/* Planet ring animation - using outline circles with symbols like landing page */}
      <div className="relative w-64 h-64 mb-8">
        {planetOrder.map((planetKey, index) => {
          const angle = (index / planetOrder.length) * 2 * Math.PI - Math.PI / 2;
          const radius = 100;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const color = planetColors[planetKey];
          const symbol = planetSymbols[planetKey];

          return (
            <div
              key={planetKey}
              className="absolute w-12 h-12 rounded-full flex items-center justify-center transition-all duration-700 animate-pulse"
              style={{
                left: `calc(50% + ${x}px - 24px)`,
                top: `calc(50% + ${y}px - 24px)`,
                border: `2.5px solid ${color}`,
                backgroundColor: 'transparent',
                boxShadow: `0 0 12px ${color}70, 0 0 25px ${color}40`,
                animationDelay: `${index * 0.1}s`,
                animationDuration: '2s'
              }}
            >
              <span 
                className="text-lg font-medium"
                style={{ 
                  color: color,
                  textShadow: `0 0 8px ${color}60`
                }}
              >
                {symbol}
              </span>
            </div>
          );
        })}

        {/* Center glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className="w-16 h-16 rounded-full transition-all duration-1000"
            style={{
              background: `radial-gradient(circle, ${planetColors.sun}25 0%, ${planetColors.sun}10 40%, transparent 70%)`,
              filter: 'blur(25px)',
              animation: 'pulse-glow 3s ease-in-out infinite'
            }}
          />
        </div>
      </div>

      {/* Loading message */}
      <div className="text-center">
        <h3 className="text-xl font-heading text-white mb-2 tracking-luxury-tight font-semibold">
          {message}
        </h3>
        <p className="text-text-secondary text-sm font-elegant tracking-luxury-tight italic">
          The celestial forces are aligning...
        </p>
      </div>

      {/* Twinkling stars background effect */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default PlanetLoader;
